// @flow
import { SHAPES } from '../../server/shared/constants/zoneConstants';
import type { Action, Zone, Shape, ShapeType } from '../types';
import * as constants from '../constants/zoneConstants';

export type State = $ReadOnly<{
    lists: {
        permanent: $ReadOnlyArray<$PropertyType<Zone, 'id'>>,
        temporary: $ReadOnlyArray<$PropertyType<Zone, 'id'>>
    },
    entities: $ReadOnly<{
        [id: string]: Zone
    }>,
    status: $ReadOnly<{
        isLoading: boolean,
        error: boolean
    }>,
    drawing: null | $ReadOnly<{
        zone?: Zone,
        type: ShapeType,
        shape: null | Shape
    }>
}>;

const emptyPolygonShape = {
    coordinates: [[]]
};

const emptyCircleShape = {
    center: [],
    radius: null
};

const initialState = {
    lists: {
        permanent: [],
        temporary: []
    },
    entities: {},
    status: {
        isLoading: false,
        error: false
    },
    drawing: null
};

export default function zones(state: State = initialState, action: Action): State {
    switch(action.type) {
        case constants.LOAD_ZONES: {
            const lists = {
                permanent: [],
                temporary: []
            };
            const entities = {};

            for(let i = 0, length = action.data.length; i < length; i += 1) {
                const zone = action.data[i];
                const id = zone.id;
                const type = zone.type;

                if(!lists[type]) {
                    throw new Error(`Unknown zone type ${type}`);
                }

                lists[type].push(id);
                entities[id] = zone;
            }

            return {
                ...state,
                lists,
                entities
            };
        }

        case constants.SET_ZONE: {
            const id = action.data.id;
            const type = action.data.type;
            let list = state.lists[type];

            if(!list) {
                throw new Error(`Unknown zone type ${type}`);
            }

            if(list.indexOf(id) === -1) {
                list = [
                    ...list,
                    id
                ];
            }

            return {
                ...state,
                lists: {
                    ...state.lists,
                    [type]: list
                },
                entities: {
                    ...state.entities,
                    [id]: action.data
                }
            };
        }

        case constants.UNSET_ZONE: {
            const id = action.data.id;
            const type = action.data.type;
            let list = state.lists[type];

            if(!list) {
                throw new Error(`Unknown zone type ${type}`)
            }

            let isChanged = false;
            const index = list.indexOf(id);
            if(index !== -1) {
                isChanged = true;
                list = [].concat(list);
                list.splice(index, 1);
            }

            let entities = state.entities;
            if(state.entities[id]) {
                isChanged = true;
                entities = {
                    ...state.entities
                };
                delete entities[id];
            }

            if(!isChanged) {
                return state;
            }

            return {
                ...state,
                lists: {
                    ...state.lists,
                    [type]: list
                },
                entities
            };
        }

        case constants.SET_DRAWING_SHAPE_TYPE: {
            const type = action.data;
            let drawing;

            if(type === null) {
                drawing = null;
            }
            else {
                let shape;

                if(type === SHAPES.polygon || type === SHAPES.line) {
                    shape = {
                        ...emptyPolygonShape
                    };
                }
                else if(type === SHAPES.circle) {
                    shape = {
                        ...emptyCircleShape
                    }
                }
                else {
                    throw new Error(`Unknown shape type ${type}`);
                }

                drawing = {
                    type,
                    shape
                };
            }

            return {
                ...state,
                drawing
            };
        }

        case constants.SET_DRAWING_SHAPE:
            return {
                ...state,
                drawing: {
                    ...state.drawing,
                    shape: action.data
                }
            };

        case constants.SET_DRAWING_ZONE:
            return {
                ...state,
                drawing: {
                    ...state.drawing,
                    zone: action.data
                }
            };

        default:
            return state;
    }
}